import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Rocket, FileText, Users, Settings, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useNavigate } from 'react-router-dom';
import { requiresPayment, getPaymentStatusMessage } from '../../utils/paymentUtils';
import PaymentRequiredModal from '../PaymentRequiredModal';

const CompletionStep = ({ onNext }) => {
  const { user, completeOnboardingStep } = useAuth();
  const { completeStep, userData } = useOnboarding();
  const navigate = useNavigate();
  const [isCompleting, setIsCompleting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleCreateDocument = () => {
    // Check if user has active payment before allowing document creation
    if (requiresPayment(user)) {
      setShowPaymentModal(true);
      return;
    }
    
    navigate('/new-document');
  };

  const quickActions = [
    {
      icon: <FileText className="w-6 h-6 text-blue-500" />,
      title: "Upload your first document",
      description: "Start by uploading a document to get signatures",
      action: "Create Document",
      onClick: handleCreateDocument
    },
    {
      icon: <Users className="w-6 h-6 text-green-500" />,
      title: "Invite team members",
      description: "Add colleagues to collaborate on documents",
      action: "Manage Team",
      onClick: () => navigate('/settings?tab=team')
    },
    {
      icon: <Settings className="w-6 h-6 text-purple-500" />,
      title: "Customize your settings",
      description: "Fine-tune your preferences and integrations",
      action: "Open Settings",
      onClick: () => navigate('/settings')
    }
  ];

  const handleComplete = async () => {
    setIsCompleting(true);
    
    try {
      console.log('🚀 CompletionStep: Starting final onboarding completion...');
      
      // Complete the final onboarding step
      await completeOnboardingStep('completion');
      completeStep('completion');
      
      console.log('✅ CompletionStep: Onboarding completion API call successful');
      
      // Add a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call onNext to trigger completion handlers
      onNext();
      
      console.log('✅ CompletionStep: Onboarding flow completed successfully');
    } catch (error) {
      console.error('❌ CompletionStep: Error completing onboarding:', error);
      // CRITICAL: Do NOT proceed if final step fails - this could cause the bug
      // Instead, show error to user and let them retry
      alert('Failed to complete onboarding setup. Please check your connection and try again.');
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-12 h-12 text-green-500" />
        </motion.div>
        
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          🎉 You're all set, {user?.name?.split(' ')[0]}!
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Welcome to SignPact! Your account is now configured and ready to streamline your document workflow.
        </p>
      </motion.div>

      {/* Setup Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-gray-50 rounded-xl p-6 border border-gray-100 mb-8"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Your setup summary:
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-700">Profile completed</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-700">Preferences configured</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-700">Features explored</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium text-gray-700">Company:</span>
              <span className="text-gray-600 ml-2">{userData.companyName}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700">Role:</span>
              <span className="text-gray-600 ml-2">{userData.jobTitle}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700">Use case:</span>
              <span className="text-gray-600 ml-2">{userData.useCase}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mb-8"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Ready to get started? Here are some quick actions:
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors group"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {action.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {action.title}
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {action.description}
                  </p>
                  <button 
                    onClick={action.onClick}
                    className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700 font-medium group-hover:underline"
                  >
                    <span>{action.action}</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Completion Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="text-center"
      >
        <button
          onClick={handleComplete}
          disabled={isCompleting}
          className="flex items-center space-x-3 mx-auto bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          <Rocket className="w-5 h-5" />
          <span>
            {isCompleting ? 'Completing Setup...' : 'Go to Dashboard'}
          </span>
        </button>
        
        <p className="text-sm text-gray-500 mt-4">
          You can always access these features and more from your dashboard
        </p>
      </motion.div>

      {/* Completion Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1 }}
        className="mt-8 text-center"
      >
        <div className="border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-600">
            You're all set! Start creating and managing your documents with SignPact.
          </p>
        </div>
      </motion.div>

      {/* Payment Required Modal */}
      {showPaymentModal && (
        <PaymentRequiredModal 
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          message={getPaymentStatusMessage(user).message}
          redirectPath={getPaymentStatusMessage(user).redirectPath}
          isFreeTrialMessage={getPaymentStatusMessage(user).isFreeTrialMessage}
        />
      )}
    </div>
  );
};

export default CompletionStep;